import bwipjs from 'bwip-js/browser';
import { Editor, Plugin } from 'grapesjs';
import mustache from 'mustache';
import { MutableRefObject } from 'react';
import { type BarcodeOrgInfo, getBarcodeProps } from 'src/modules/palletTags';
import { testData } from '../pages/PalletTagEditorPage/PalletTagEditorPage';

const plugin: (orgInfo: Pick<MutableRefObject<BarcodeOrgInfo>, 'current'>) => Plugin<{}> =
  (orgInfo) => (editor: Editor) => {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;
    const commands = editor.Commands;

    commands.add('update-layout', {
      run(em, _, opts) {
        const component = em.DomComponents.getWrapper().find('#tag-layout')[0];

        component.setStyle(
          { width: `${opts.width}in`, height: `${opts.height}in`, padding: `${opts.padding}px` },
          { inline: true }
        );
      },
    });

    blockManager.add('Column', {
      label: 'Column',
      category: 'Layout',
      content: [
        {
          type: 'div',
          attributes: { class: 'flex-column' },
          styles: `.flex-column { display: flex; flex-direction: column }`,
        },
      ],
    });

    blockManager.add('Row', {
      label: 'Row',
      category: 'Layout',
      content: [
        { type: 'div', attributes: { class: 'flex-row' }, styles: `.flex-row { display: flex; flex-direction: row }` },
      ],
    });

    blockManager.add('Border Box', {
      label: 'Border Box',
      category: 'Layout',
      content: [
        {
          type: 'div',
          attributes: { class: 'border-box' },
          styles: `.border-box { border: 1px solid black; border-radius: 4px;     }`,
        },
      ],
    });

    blockManager.add('Text', { label: 'Text', category: 'Basic', content: [{ type: 'text', content: 'Text' }] });

    blockManager.add('Barcode', {
      label: 'Barcode',
      category: 'Basic',
      content: [{ type: 'barcode', content: 'Barcode' }],
    });

    blockManager.add('pickcode-block', {
      label: 'Pick Code Block',
      category: 'Components',
      content: [
        {
          type: 'div',
          attributes: { class: 'pickcode-outer' },
          styles: `
          .pickcode-outer {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-left: -5px;
            padding-left: 1px;
            padding-right: 2px;
          }
        `,
          components: [
            {
              type: 'div',
              attributes: { class: 'pickcode-inner' },
              styles: `
              .pickcode-inner {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: baseline;
                background-color: black;
                color: white;
                width: 65px;
                height: 30px;
                gap: 0.5px;
              }
            `,
              components: [
                {
                  type: 'variable',
                  variable: 'pickCode.small',
                  attributes: { class: 'pickcode-small' },
                  styles: `.pickcode-small { font-size: 15px; font-weight: bold; }`,
                },
                {
                  type: 'variable',
                  variable: 'pickCode.large',
                  attributes: { class: 'pickcode-large' },
                  styles: `.pickcode-large { font-size: 25px; font-weight: bold; line-height: 33px; }`,
                },
              ],
            },
          ],
        },
      ],
    });

    editor.DomComponents.addType('barcode', {
      model: {
        defaults: {
          tagName: 'barcode',
          traits: [
            { type: 'text', label: 'Value', name: 'value', changeProp: true, default: '{{ tagNumber }}' },
            {
              type: 'select',
              name: 'tagtype',
              label: 'Barcode Type',
              changeProp: true,
              options: [
                { id: 'upc', name: 'UPC' },
                { id: 'gs1', name: 'GS1' },
                { id: 'sscc18', name: 'SSCC 18' },
                { id: 'code128', name: 'Code 128' },
                { id: 'qrcode', name: 'QR Code' },
                { id: 'datamatrix', name: 'Datamatrix' },
              ],
              default: 'code128',
            },
            { type: 'text', label: 'Text', name: 'text', changeProp: true, default: '{{ tagNumber }}' },
            { type: 'text', label: 'Scale', name: 'scale', changeProp: true, default: 3 },
            { type: 'text', label: 'Text Size', name: 'textsize', changeProp: true, default: 5.5 },
            { type: 'text', label: 'Ink Spread', name: 'inkspread', changeProp: true },
            { type: 'text', label: 'Bar Code Height', name: 'barcodeheight', changeProp: true, default: 5 },
            { type: 'text', label: 'Number System (UPC)', name: 'numbersystem', changeProp: true, default: '' },
            { type: 'text', label: 'Height', name: 'height', changeProp: true, default: '100%' },
            { type: 'text', label: 'Width', name: 'width', changeProp: true, default: '100%' },
            {
              type: 'select',
              name: 'rotation',
              label: 'Rotation',
              changeProp: true,
              options: [
                { id: 'N', name: 'None' },
                { id: 'R', name: 'Right' },
                { id: 'L', name: 'Left' },
                { id: 'I', name: 'Inverted' },
              ],
            },
          ],
          attributes: {
            'data-gjs-type': 'barcode',
            tagtype: 'code128',
            value: '{{ tagNumber }}',
            text: '{{ tagNumber }}',
            textsize: 5.5,
            barcodeheight: 5,
            scale: 3,
            inkspread: 0.5,
            width: '100%',
            height: '100%',
            rotation: 'N',
          },
        },
        init() {
          const syncAttrs = [
            'value',
            'text',
            'scale',
            'textsize',
            'inkspread',
            'barcodeheight',
            'numbersystem',
            'height',
            'width',
            'tagtype',
            'rotation',
          ];

          syncAttrs.forEach((attr) => {
            this.on(`change:${attr}`, () => {
              const value = this.get(attr);
              const attrs = this.getAttributes();

              this.set('attributes', { ...attrs, [attr]: value != null ? value : undefined });
              this.handleValueChange();
            });
          });
        },
        handleValueChange() {
          this.view.render();
        },
      },
      view: {
        onRender: function () {
          const scale = this.model.get('scale');
          const textsize = this.model.get('textsize');
          const inkspread = this.model.get('inkspread');
          const barCodeHeight = this.model.get('barcodeheight');
          const height = this.model.get('height');
          const width = this.model.get('width');
          const tagType = this.model.get('tagtype');
          const value = this.model.get('value');
          const text = this.model.get('text');
          const numberSystem = this.model.get('numbersystem');
          const rotation = this.model.get('rotation');

          const { lot, gtinCode, rawDate, productCode } = testData ?? {};
          const valueRendered = mustache.render(value, testData);
          const textRendered = mustache.render(text, testData);

          const barcodeProps = getBarcodeProps(valueRendered, {
            type: tagType,
            textTemplate: textRendered,
            orgInfo: orgInfo.current,
            packDate: rawDate,
            gtinCode,
            numberSystem,
            lotNumber: lot,
            productCode,
          });

          const svg = bwipjs.toSVG({
            scale,
            textsize,
            inkspread,
            height: barCodeHeight,
            rotate: rotation,
            ...barcodeProps,
          });

          this.el.innerHTML = `
        <div display="flex" width="${width}" height="${height}" flexGrow="1" justifyContent="center">
        ${svg}
        </div>
        `;
        },
      },
    });

    editor.DomComponents.addType('text', { model: { defaults: { tagName: 'span' } } });

    blockManager.add('variable', {
      label: 'Variable',
      content: `<div data-gjs-type="variable"></div>`,
      media: `
        <svg viewBox="0 0 24 24">
          <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
        </svg>
      `,
      category: 'Variables',
      attributes: { variablePath: null },
    });

    const variableTraits = (variable?: string) => [
      { type: 'text', label: 'Variable', name: 'variable', value: variable, changeProp: true },
      { type: 'text', label: 'Prefix', name: 'prefix', changeProp: true },
      { type: 'text', label: 'Suffix', name: 'suffix', changeProp: true },
    ];

    domComponents.addType('variable', {
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,
          draggable: true,
          attributes: { 'data-gjs-type': 'variable' },
          traits: variableTraits(),
        },
        init() {
          this.listenTo(this, 'change:variable', this.handleContentChange);
          this.listenTo(this, 'change:prefix', this.handleContentChange);
          this.listenTo(this, 'change:suffix', this.handleContentChange);
          this.listenTo(this, 'change:variant', this.handleTypographyClassChange);
        },
        handleContentChange() {
          this.view.render();
        },
        toHTML: function () {
          const prefix = this.get('prefix') ?? '';
          const suffix = this.get('suffix') ?? '';
          const variable = this.get('variable');
          const classes = this.get('classes').pluck('name').join(' ');

          return `<div id="${this.ccid}">
          <span class="${classes}">${prefix}{{{${variable}}}}${suffix}</span>
        </div>`;
        },
      },
      view: {
        onRender: function () {
          const prefix = this.model.get('prefix') || '';
          const suffix = this.model.get('suffix') || '';
          const variable = this.model.get('variable') || '';

          this.el.innerHTML =
            mustache.render(`${prefix}{{{${variable}}}}${suffix}`, testData) || '<i>select variable</i>';
        },
      },
    });

    blockManager.add('table', {
      label: 'Table',
      category: 'Components',
      content: [
        {
          type: 'table',
          components: [
            {
              type: 'tr',
              components: [
                { type: 'td-cell', components: [{ type: 'text', content: 'Cell A1' }] },
                { type: 'td-cell', components: [{ type: 'text', content: 'Cell A2' }] },
              ],
            },
            {
              type: 'tr',
              components: [
                {
                  type: 'td-cell',
                  traits: [{ type: 'number', name: 'colspan', label: 'Colspan', value: 2 }],
                  attributes: { colspan: '2' },
                  components: [{ type: 'text', content: 'Spanned across 2 columns' }],
                },
              ],
            },
          ],
        },
      ],
    });

    editor.DomComponents.addType('tr', { extend: 'default', model: { defaults: { tagName: 'tr' } } });

    editor.DomComponents.addType('td-cell', {
      extend: 'default',
      model: {
        defaults: {
          tagName: 'td',
          traits: [
            { type: 'number', name: 'colspan', label: 'Colspan' },
            { type: 'number', name: 'rowspan', label: 'Rowspan' },
          ],
        },
        init() {
          this.on('change:attributes:colspan change:attributes:rowspan', () => {
            this.view && this.view.render();
          });
        },
      },
    });
  };

export default plugin;
